import React, { useRef } from 'react'

/**
 * A floating toolbar is rendered via a portal straight onto `document.body`, outside the canvas
 * scroller's DOM subtree — so the scroller's own ctrl/pinch-zoom `wheel` listener (which calls
 * preventDefault to stop native zoom) never sees wheel events that land on the toolbar. Without
 * this, ctrl+scroll/pinch-zoom while hovering the toolbar falls through to the browser's native
 * page zoom instead of doing nothing (or being ignored, same as hovering any other non-canvas
 * chrome).
 *
 * Wired via a callback ref rather than a `useEffect(..., [])` — the toolbar returns `null` (no
 * portal DOM at all) until `displayPos` is computed on a follow-up render, so a plain effect with
 * an empty dependency array fires once against a still-null `rootRef.current` and never gets a
 * second chance to attach once the DOM actually exists. A callback ref is invoked by React
 * exactly when the node mounts/unmounts, so it can't miss that window.
 *
 * Returns the callback ref to put on the portal root, plus `rootRef` for reading the node (the
 * tooltips' `container`, the blur check).
 */
export function useBlockBrowserZoomRef() {
  const rootRef = useRef<HTMLDivElement | null>(null)
  const wheelCleanupRef = useRef<(() => void) | null>(null)

  const setRootRef = React.useCallback((el: HTMLDivElement | null) => {
    rootRef.current = el
    wheelCleanupRef.current?.()
    wheelCleanupRef.current = null

    if (!el) return

    const blockBrowserZoom = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) e.preventDefault()
    }

    el.addEventListener('wheel', blockBrowserZoom, { passive: false })
    wheelCleanupRef.current = () => el.removeEventListener('wheel', blockBrowserZoom)
  }, [])

  return { rootRef, setRootRef }
}
